import { Link } from 'react-router';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import {
  ArrowRight,
  Bot,
  Code2,
  Wrench,
  Zap,
  CheckCircle,
  ArrowDownToLine,
  Search,
  Headphones,
  Settings,
  Share2,
  Users,
  ClipboardCheck,
  Globe,
  Activity,
  ShieldCheck,
} from 'lucide-react';

const services = [
  { name: 'PLC Programming', desc: 'Control logic for industrial automation systems', icon: Code2 },
  { name: 'Robot Programming', desc: 'KUKA, ABB, FANUC robotic system programming', icon: Bot },
  { name: 'Mechanical Support', desc: 'Precision mechanical assembly and maintenance', icon: Wrench },
  { name: 'Electrical Support', desc: 'Industrial electrical systems and diagnostics', icon: Zap },
  { name: 'Commissioning', desc: 'System startup, testing, and validation', icon: CheckCircle },
  { name: 'Installation', desc: 'Equipment installation and line integration', icon: ArrowDownToLine },
  { name: 'Fault Finding', desc: 'Diagnostic expertise for critical system failures', icon: Search },
  { name: 'After-Sales Support', desc: 'Post-deployment technical assistance', icon: Headphones },
  { name: 'Maintenance', desc: 'Preventive and corrective maintenance programs', icon: Settings },
  { name: 'Systems Integration', desc: 'Multi-vendor system architecture and integration', icon: Share2 },
  { name: 'Technical Workforce', desc: 'Global deployment of qualified specialists', icon: Users },
  { name: 'Project Management', desc: 'End-to-end industrial project execution', icon: ClipboardCheck },
];

const countries = [
  { name: 'South Africa', region: 'Headquarters / Africa Operations', flag: '🇿🇦' },
  { name: 'United States', region: 'Operational', flag: '🇺🇸' },
  { name: 'Namibia', region: 'Expansion Target', flag: '🇳🇦' },
  { name: 'Canada', region: 'Expansion Target', flag: '🇨🇦' },
  { name: 'Germany', region: 'Expansion Target', flag: '🇩🇪' },
];

const commandFeatures = [
  { label: '24/7 Global Operations', icon: Globe },
  { label: 'Real-time System Monitoring', icon: Activity },
  { label: 'Rapid Response Teams', icon: Zap },
  { label: 'Secure. Reliable. Controlled.', icon: ShieldCheck },
];

export default function Home() {
  useDocumentTitle('Straton Group — Industrial Automation & Systems Integration');

  return (
    <main id="main-content">
      {/* ── Hero Section ── */}
      <section className="relative min-h-[85vh] lg:min-h-screen flex items-end">
        <div className="absolute inset-0">
          <img
            src="/home-hero.webp" fetchPriority="high"
            alt="Industrial robotic arm"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-bg-primary via-bg-primary/80 to-bg-primary/30" />
        </div>

        <div className="relative z-10 w-full max-w-[1400px] mx-auto section-padding pt-24 lg:pt-32 pb-20 lg:pb-28">
          <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold uppercase tracking-[0.15em] text-text-primary mb-6 leading-tight">
            We Build Systems<br />
            That Move Industry<br />
            Forward.
          </h1>

          <p className="text-sm md:text-base text-text-secondary mb-8 max-w-lg">
            Industrial automation. Systems integration. Technical deployment.
          </p>

          <Link
            to="/solutions"
            className="inline-flex items-center gap-3 px-8 py-3.5 border border-border-primary text-text-secondary text-xs font-medium uppercase tracking-widest hover:border-text-muted hover:text-text-primary transition-colors"
          >
            <span>DISCOVER MORE</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ── Ticker Bar ── */}
      <div className="border-t border-b border-border-secondary bg-bg-primary">
        <div className="max-w-[1400px] mx-auto section-padding py-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Bot className="w-4 h-4 text-text-muted" strokeWidth={1.5} />
          </div>
          <div className="flex items-center gap-6">
            <span className="text-xs uppercase tracking-widest text-text-muted">INDUSTRIAL AUTOMATION</span>
            <span className="text-xs text-text-muted hidden md:block">|</span>
            <span className="text-xs uppercase tracking-widest text-text-muted">SYSTEMS INTEGRATION</span>
            <span className="text-xs text-text-muted hidden md:block">|</span>
            <span className="text-xs uppercase tracking-widest text-text-muted">GLOBAL DEPLOYMENT</span>
          </div>
        </div>
      </div>

      {/* ── Services Section ── */}
      <section id="services" className="bg-bg-primary section-padding py-16 lg:py-24">
        <div className="max-w-[1400px] mx-auto">
          <span className="text-xs uppercase tracking-[0.2em] text-text-muted block mb-4">
            CAPABILITIES
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold uppercase tracking-[0.15em] text-text-primary mb-10">
            Our Services
          </h2>

          <div className="border border-border-secondary">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border-secondary">
              {services.map((service) => (
                <div
                  key={service.name}
                  className="bg-bg-primary p-6 lg:p-8 flex items-start gap-4"
                >
                  <service.icon
                    className="w-5 h-5 text-text-muted shrink-0 mt-0.5"
                    strokeWidth={1.5}
                  />
                  <div>
                    <h3 className="text-sm font-medium text-text-primary mb-1">
                      {service.name}
                    </h3>
                    <p className="text-xs text-text-muted leading-relaxed">
                      {service.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Operations Worldwide ── */}
      <section className="bg-bg-primary section-padding pb-20">
        <div className="max-w-[1400px] mx-auto">
          {/* Intro */}
          <div className="mb-12 lg:mb-16">
            <span className="text-xs uppercase tracking-[0.2em] text-text-muted block mb-4">
              GLOBAL REACH
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold uppercase tracking-[0.15em] text-text-primary mb-6">
              Operations Worldwide
            </h2>
            <p className="body-text max-w-2xl">
              Straton Group is headquartered in South Africa and has officially launched operations in the United States, expanding our footprint into North America. Wherever we operate, we maintain the same standard of precision, reliability, and technical excellence.
            </p>
          </div>

          {/* Map + presence */}
          <div className="border border-border-secondary grid grid-cols-1 lg:grid-cols-5">
            <div className="lg:col-span-3 border-b lg:border-b-0 lg:border-r border-border-secondary">
              <img
                src="/home-map.webp" loading="lazy"
                alt="Global operations map"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="lg:col-span-2 flex flex-col">
              <div className="p-5 lg:p-6 border-b border-border-secondary">
                <span className="text-[10px] uppercase tracking-[0.2em] text-text-muted">
                  Our Presence
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-px bg-border-secondary flex-1">
                {countries.map((country) => (
                  <div
                    key={country.name}
                    className="bg-bg-primary p-5 lg:p-6 flex items-center gap-4"
                  >
                    <span className="text-lg shrink-0 leading-none">{country.flag}</span>
                    <div>
                      <span className="text-sm font-medium text-text-primary block">
                        {country.name}
                      </span>
                      <span className="text-xs text-text-muted">{country.region}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Facility CTA banner */}
          <div className="mt-12 lg:mt-16 border border-border-secondary overflow-hidden relative min-h-[280px] lg:min-h-[320px]">
            <img
              src="/home-building.webp" loading="lazy"
              alt="Straton Group facility"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-bg-primary via-bg-primary/85 to-bg-primary/40" />
            <div className="relative z-10 h-full flex items-center p-8 lg:p-12">
              <div className="max-w-lg">
                <span className="text-[10px] uppercase tracking-[0.2em] text-text-muted block mb-3">
                  READY TO DEPLOY?
                </span>
                <h3 className="text-xl md:text-2xl lg:text-3xl text-text-primary mb-6 leading-snug">
                  Connect with our operations team to discuss your industrial requirements.
                </h3>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-3 px-8 py-3.5 border border-border-primary text-text-secondary text-xs font-medium uppercase tracking-widest hover:border-text-muted hover:text-text-primary transition-colors"
                >
                  <span>CONTACT STRATON</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Command Center ── */}
      <section className="bg-bg-primary section-padding pb-20">
        <div className="max-w-[1400px] mx-auto">
          <div className="border border-border-secondary grid grid-cols-1 lg:grid-cols-2">
            {/* Left — Image */}
            <div className="relative min-h-[300px] lg:min-h-[500px]">
              <img
                src="/home-command.webp" loading="lazy"
                alt="Straton Command Center"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-bg-primary/60 to-transparent" />
            </div>

            {/* Right — Content */}
            <div className="p-10 lg:p-16 flex flex-col justify-center">
              <span className="text-[10px] uppercase tracking-[0.2em] text-text-muted block mb-4">
                COMMAND CENTER
              </span>
              <h2 className="text-2xl md:text-3xl lg:text-4xl text-text-primary mb-10 leading-snug">
                Real-time oversight. Global operations. Absolute control.
              </h2>

              <div className="space-y-0">
                {commandFeatures.map((feature, i) => (
                  <div
                    key={feature.label}
                    className={`flex items-center gap-4 py-4 ${
                      i < commandFeatures.length - 1
                        ? 'border-b border-border-secondary'
                        : ''
                    }`}
                  >
                    <feature.icon
                      className="w-5 h-5 text-text-muted shrink-0"
                      strokeWidth={1.5}
                    />
                    <span className="text-sm text-text-secondary">
                      {feature.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
